import React, { Suspense } from "react";
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet,
  useLocation,
} from "react-router-dom";
import type { RouteObject } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";

import { queryClient } from "@/lib/react-query";
import { ThemeProvider } from "@/design-system/themes";
import { traceClient } from "@/services/tracing/TraceClient";
import { useAuth } from "@/stores/auth/auth-store";
import { useAuthSession } from "@/hooks/useAuthSession";
import { useMentionOrganizationSync } from "@/hooks/useMentionOrganizationSync";
import { Toaster } from "sonner";

// Re-authentication Modal
import { ReAuthModal } from "@/components/auth/ReAuthModal";
import { InactivityWarningModal } from "@/components/auth/InactivityWarningModal";
import { PublicRoute } from "@/components/auth/PublicRoute";
import { PublicAuthChallengeProvider } from "@/components/auth/PublicAuthChallengeProvider";

// Layout Components
import { RootLayout } from "@/components/layout/RootLayout";
import { AuthLayout } from "@/components/layout/AuthLayout";
import { EditorLayout } from "@/components/layout/EditorLayout";
import { ViewerLayout } from "@/components/layout/ViewerLayout";

// Page Components
import { LoginPage } from "@/pages/LoginPage";
import { RegisterPage } from "@/pages/RegisterPage";
import { ForgotPasswordPage } from "@/pages/ForgotPasswordPage";
import { ResetPasswordPage } from "@/pages/ResetPasswordPage";
import { CheckEmailPage } from "@/pages/CheckEmailPage";
import { VerifyEmailPage } from "@/pages/VerifyEmailPage";
import { DashboardPage } from "@/pages/DashboardPage";
import { EditorPage } from "@/pages/EditorPage";
import { ViewerPageWrapper } from "@/pages/ViewerPage";
import { TemplateViewerPage } from "@/pages/TemplateViewerPage";
import { ProjectsPage } from "@/pages/ProjectsPage";
import { SettingsPage } from "@/pages/SettingsPage";
import { OrgSettingsPage } from "@/pages/OrgSettingsPage";
import { ProfilePage } from "@/pages/ProfilePage";
import { DataPage } from "@/pages/DataPage";
import { AdminPage } from "@/pages/AdminPage";
import { FeedbackPage } from "@/pages/admin/FeedbackPage";
import { AgentsPage } from "@/pages/AgentsPage";
import { DataSourceWizard } from "@/pages/DataSourceWizard";
import { DataSourceDetailPage } from "@/pages/DataSourceDetailPage";
import { TableFieldsEditor } from "@/pages/TableFieldsEditor";
import { NotFoundPage } from "@/pages/NotFoundPage";
import { FileManagerPage } from "@/pages/FileManagerPage";
import { DevStoragePage } from "@/pages/DevStoragePage";
import { WorkflowTestPage } from "@/pages/WorkflowTestPage";
import { WorkflowConfigurationPage } from "@/pages/WorkflowConfigurationPage";
const WorkflowEditorPage = React.lazy(() =>
  import("@/pages/WorkflowEditorPage").then((m) => ({ default: m.WorkflowEditorPage }))
);
const HAEntityBrowserPage = React.lazy(() =>
  import("@/pages/HAEntityBrowserPage").then((m) => ({
    default: m.HAEntityBrowserPage,
  }))
);
import { AnalyticsTestPage } from "@/components/analytics/AnalyticsTestPage";
import { LandingPageV1 } from "@/pages/LandingPageV1";
import { LandingPageV3 } from "@/pages/LandingPageV3";
import { ContactPage } from "@/pages/ContactPage";
import { AboutPage } from "@/pages/AboutPage";
import { BlogPage } from "@/pages/BlogPage";
import { CareersPage } from "@/pages/CareersPage";
import { AIUsageHistoryPage } from "@/pages/AIUsageHistoryPage";
import { DeviceManagementPage } from "@/pages/DeviceManagementPage";
import { HydrawiseDashboardPage } from "@/integrations/hydrawise";
import { BinaryFormatsPage } from "@/pages/BinaryFormatsPage";
import { InfrastructureDashboard } from "@/pages/infrastructure/InfrastructureDashboard";
import { AcceptInvitationPage } from "@/pages/invitations/AcceptInvitationPage";
import { DeclineInvitationPage } from "@/pages/invitations/DeclineInvitationPage";
import { ConnectionsPage } from "@/pages/ConnectionsPage";
import { AcceptConnectionPage } from "@/pages/connections/AcceptConnectionPage";
import { DeclineConnectionPage } from "@/pages/connections/DeclineConnectionPage";
import { UserPublicProfilePage } from "@/pages/UserPublicProfilePage";
import { MessagesPage } from "@/pages/MessagesPage";
// Customer Onboarding
import { CustomerOnboardingPage } from "@/pages/onboarding/CustomerOnboardingPage";

// Billing
import { PricingPage } from "@/pages/PricingPage";
import { BillingPage } from "@/pages/BillingPage";
import { PeoplePage } from "@/pages/account/PeoplePage";
import { GraduatePage } from "@/pages/account/GraduatePage";
import { legalRoutes } from "@/pages/legalRoutes";

// Service Provider Pages
import ServiceProviderSetupPage from "@/pages/ServiceProviderSetupPage";
import ServiceProviderDashboardPage from "@/pages/ServiceProviderDashboardPage";
import ClientProjectSetupPage from "@/pages/ClientProjectSetupPage";
import ClientProjectDetailPage from "@/pages/ClientProjectDetailPage";

// Error Boundary
import UserFlowErrorBoundary from "@/components/error/UserFlowErrorBoundary";

// Permission Provider
import { PermissionProvider } from "@/contexts/PermissionContext";

// Google Analytics
import { GoogleAnalyticsTracker } from "@/hooks/useGoogleAnalytics";
import { GlobalExpandableChat } from "@/components/chat/GlobalExpandableChat";

// Scroll to top on route change
const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// Wrapper component to manage dialog state for editor
const EditorPageWrapper: React.FC = () => {
  const [showSharingDialog, setShowSharingDialog] = React.useState(false);
  const [showPermissionManager, setShowPermissionManager] =
    React.useState(false);
  const [showVersioningPanel, setShowVersioningPanel] = React.useState(false);
  const [isVersioningMode, setIsVersioningMode] = React.useState(false);

  const handleOpenSharing = () => setShowSharingDialog(true);
  const handleOpenPermissionMatrix = () => setShowPermissionManager(true);
  const handleOpenProjectSettings = () => {
    // TODO: Implement project settings dialog
  };

  const handleToggleVersioning = React.useCallback(() => {
    setShowVersioningPanel((prev) => !prev);
  }, []);

  const handleExitVersioningMode = React.useCallback(() => {
    setIsVersioningMode(false);

    // Return focus to versioning button for accessibility
    setTimeout(() => {
      const versioningButton = document.querySelector(
        '[aria-label*="Enter version control mode"]',
      );
      if (versioningButton && versioningButton instanceof HTMLElement) {
        versioningButton.focus();
      }
    }, 100);
  }, []);

  // Keyboard shortcuts for versioning
  React.useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      // Ctrl/Cmd + Shift + V to toggle versioning mode
      if (
        (event.ctrlKey || event.metaKey) &&
        event.shiftKey &&
        event.key === "V"
      ) {
        event.preventDefault();
        handleToggleVersioning();
      }
      // ESC to exit versioning mode (global handler)
      if (event.key === "Escape" && isVersioningMode) {
        handleExitVersioningMode();
      }
    };

    document.addEventListener("keydown", handleKeyPress);
    return () => document.removeEventListener("keydown", handleKeyPress);
  }, [isVersioningMode, handleToggleVersioning, handleExitVersioningMode]);

  return (
    <PermissionProvider>
      <EditorLayout
        onOpenSharing={handleOpenSharing}
        onOpenPermissionMatrix={handleOpenPermissionMatrix}
        onOpenProjectSettings={handleOpenProjectSettings}
        onToggleVersioning={handleToggleVersioning}
        isVersioningOpen={showVersioningPanel}
        isVersioningMode={isVersioningMode}
      >
        <EditorPage
          showSharingDialog={showSharingDialog}
          setShowSharingDialog={setShowSharingDialog}
          showPermissionManager={showPermissionManager}
          setShowPermissionManager={setShowPermissionManager}
          onOpenSharing={handleOpenSharing}
          onOpenPermissionMatrix={handleOpenPermissionMatrix}
          onOpenProjectSettings={handleOpenProjectSettings}
          onToggleVersioning={handleToggleVersioning}
          onExitVersioningMode={handleExitVersioningMode}
          isVersioningOpen={showVersioningPanel}
          isVersioningMode={isVersioningMode}
        />
      </EditorLayout>
    </PermissionProvider>
  );
};

// Wrapper component for viewer with dialog state management
const ViewerPageWrapperWithLayout: React.FC = () => {
  const [, setShowSharingDialog] = React.useState(false);

  const handleOpenSharing = () => setShowSharingDialog(true);
  const handleOpenSettings = () => {
    // TODO: Implement viewer settings
  };

  return (
    <PermissionProvider>
      <ViewerLayout
        onOpenSharing={handleOpenSharing}
        onOpenSettings={handleOpenSettings}
      >
        <ViewerPageWrapper />
      </ViewerLayout>
    </PermissionProvider>
  );
};

// Wrapper component for template viewer
const TemplateViewerPageWithLayout: React.FC = () => {
  return (
    <ViewerLayout isTemplateMode={true}>
      <TemplateViewerPage />
    </ViewerLayout>
  );
};

const ProtectedRouteSessionValidator: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { validateSession, extendSession } = useAuthSession();
  const [hasValidatedSession, setHasValidatedSession] = React.useState(false);

  // Validate session once on mount — no hard redirects.
  // validateSession() now opens re-auth modal for expired tokens (returns true),
  // only returns false when genuinely not authenticated.
  React.useEffect(() => {
    let isMounted = true;

    if (!hasValidatedSession) {
      validateSession()
        .then((isValid) => {
          if (!isMounted) {
            return;
          }

          setHasValidatedSession(true);
          if (isValid) {
            extendSession();
          }
        })
        .catch(() => {
          if (isMounted) {
            setHasValidatedSession(true);
          }
        });
    }

    return () => {
      isMounted = false;
    };
  }, [hasValidatedSession, validateSession, extendSession]);

  return <>{children}</>;
};

// Enhanced Protected Route Component with session validation
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated, isLoading, initialized } = useAuth();

  if (!initialized || isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        <span className="ml-2 text-muted-foreground">
          Validating session...
        </span>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return (
    <ProtectedRouteSessionValidator>
      {children}
    </ProtectedRouteSessionValidator>
  );
};

const TraceBootstrap: React.FC = () => {
  const { isAuthenticated, initialized } = useAuth();

  React.useEffect(() => {
    if (!initialized || !isAuthenticated) {
      return;
    }

    traceClient.init().catch(() => {});
  }, [initialized, isAuthenticated]);

  return null;
};

// Root component that provides providers
const AppProviders: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Sync organization context for mention system localStorage namespacing.
  useMentionOrganizationSync();

  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider defaultTheme="dark">
          <PublicAuthChallengeProvider>
            <div className="App">{children}</div>
          </PublicAuthChallengeProvider>
          <GlobalExpandableChat />
          <TraceBootstrap />

          {/* Toast notifications */}
          <Toaster position="top-right" richColors />

          {/* Re-authentication modal - overlays entire app without unmounting */}
          <ReAuthModal />

          {/* Non-blocking inactivity warning (replaces blocking confirm()) */}
          <InactivityWarningModal />
        </ThemeProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
};

// Create router with future flags to prepare for v7
const appRoutes: RouteObject[] = [
  {
    path: "/",
    element: (
      <AppProviders>
        <ScrollToTop />
        <GoogleAnalyticsTracker />
        <Outlet />
      </AppProviders>
    ),
    children: [
      // Public Routes
      {
        path: "landing/v1",
        element: <LandingPageV1 />,
      },
      {
        path: "landing/v3",
        element: <LandingPageV3 />,
      },
      {
        path: "contact",
        element: <ContactPage />,
      },
      {
        path: "about",
        element: <AboutPage />,
      },
      {
        path: "blog",
        element: <BlogPage />,
      },
      {
        path: "careers",
        element: <CareersPage />,
      },
      {
        path: "pricing",
        element: <PricingPage />,
      },
      ...legalRoutes,
      // Invitation Routes (public - accessed via email links)
      {
        path: "invitations/accept/:token",
        element: <AcceptInvitationPage />,
      },
      {
        path: "invitations/decline/:token",
        element: <DeclineInvitationPage />,
      },
      // Connection Routes (public - accessed via email links)
      {
        path: "connections/accept/:token",
        element: <AcceptConnectionPage />,
      },
      {
        path: "connections/decline/:token",
        element: <DeclineConnectionPage />,
      },
      // Customer Onboarding (public - accessed via email links)
      {
        path: "onboarding/:token",
        element: <CustomerOnboardingPage />,
      },
      {
        path: "login",
        element: (
          <PublicRoute>
            <AuthLayout>
              <LoginPage />
            </AuthLayout>
          </PublicRoute>
        ),
      },
      {
        path: "register",
        element: (
          <PublicRoute>
            <AuthLayout>
              <RegisterPage />
            </AuthLayout>
          </PublicRoute>
        ),
      },
      {
        path: "forgot-password",
        element: (
          <PublicRoute>
            <AuthLayout>
              <ForgotPasswordPage />
            </AuthLayout>
          </PublicRoute>
        ),
      },
      {
        path: "reset-password",
        element: (
          <PublicRoute allowAuthenticated>
            <AuthLayout>
              <ResetPasswordPage />
            </AuthLayout>
          </PublicRoute>
        ),
      },
      {
        path: "check-email",
        element: (
          <PublicRoute>
            <AuthLayout>
              <CheckEmailPage />
            </AuthLayout>
          </PublicRoute>
        ),
      },
      {
        path: "verify-email",
        element: (
          <PublicRoute allowAuthenticated>
            <AuthLayout>
              <VerifyEmailPage />
            </AuthLayout>
          </PublicRoute>
        ),
      },

        // Protected Routes
        {
          path: "dashboard",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <RootLayout>
                  <DashboardPage />
                </RootLayout>
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        {
          path: "dashboard/connections",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <RootLayout>
                  <ConnectionsPage />
                </RootLayout>
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        {
          path: "dashboard/messages",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <RootLayout>
                  <MessagesPage />
                </RootLayout>
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        // Service Provider Routes
        {
          path: "service-provider/setup",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <RootLayout>
                  <ServiceProviderSetupPage />
                </RootLayout>
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        {
          path: "service-provider/dashboard",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <RootLayout>
                  <ServiceProviderDashboardPage />
                </RootLayout>
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        {
          path: "service-provider/projects/new",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <RootLayout>
                  <ClientProjectSetupPage />
                </RootLayout>
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        {
          path: "service-provider/projects/:projectId",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <RootLayout>
                  <ClientProjectDetailPage />
                </RootLayout>
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        // User public profile routes
        {
          path: "users/:userId/profile",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <RootLayout>
                  <UserPublicProfilePage />
                </RootLayout>
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        {
          path: "u/:username",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <RootLayout>
                  <UserPublicProfilePage />
                </RootLayout>
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        {
          path: "projects",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <RootLayout>
                  <ProjectsPage />
                </RootLayout>
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        // User-scoped routes (Phase 1: Dual Ownership Model)
        {
          path: "users/:username/projects",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <RootLayout>
                  <ProjectsPage />
                </RootLayout>
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        // Organization-scoped routes (Phase 1: Dual Ownership Model)
        {
          path: "orgs/:orgSlug/projects",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <RootLayout>
                  <ProjectsPage />
                </RootLayout>
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        {
          path: "orgs/:orgSlug/settings",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <RootLayout>
                  <OrgSettingsPage />
                </RootLayout>
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        {
          path: "orgs/:orgSlug/integrations/ha",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <RootLayout>
                  <Suspense
                    fallback={
                      <div className="h-screen flex items-center justify-center text-muted-foreground">
                        Loading Home Assistant browser...
                      </div>
                    }
                  >
                    <HAEntityBrowserPage />
                  </Suspense>
                </RootLayout>
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        {
          path: "ai-usage",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <RootLayout>
                  <AIUsageHistoryPage />
                </RootLayout>
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        {
          path: "editor/:projectId",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <EditorPageWrapper />
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        {
          path: "editor/:projectId/page/:pageId",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <EditorPageWrapper />
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        // User-scoped editor routes
        {
          path: "users/:username/editor/:projectId",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <EditorPageWrapper />
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        {
          path: "users/:username/editor/:projectId/page/:pageId",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <EditorPageWrapper />
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        // Organization-scoped editor routes
        {
          path: "orgs/:orgSlug/editor/:projectId",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <EditorPageWrapper />
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        {
          path: "orgs/:orgSlug/editor/:projectId/page/:pageId",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <EditorPageWrapper />
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        {
          path: "viewer/:projectId",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <ViewerPageWrapperWithLayout />
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        {
          path: "viewer/:projectId/page/:pageId",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <ViewerPageWrapperWithLayout />
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        // User-scoped viewer routes
        {
          path: "users/:username/viewer/:projectId",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <ViewerPageWrapperWithLayout />
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        {
          path: "users/:username/viewer/:projectId/page/:pageId",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <ViewerPageWrapperWithLayout />
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        // Organization-scoped viewer routes
        {
          path: "orgs/:orgSlug/viewer/:projectId",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <ViewerPageWrapperWithLayout />
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        {
          path: "orgs/:orgSlug/viewer/:projectId/page/:pageId",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <ViewerPageWrapperWithLayout />
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        {
          path: "view/:projectId",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <ViewerPageWrapperWithLayout />
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        {
          path: "view/:projectId/page/:pageId",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <ViewerPageWrapperWithLayout />
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        // User-scoped view routes
        {
          path: "users/:username/view/:projectId",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <ViewerPageWrapperWithLayout />
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        {
          path: "users/:username/view/:projectId/page/:pageId",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <ViewerPageWrapperWithLayout />
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        // Organization-scoped view routes
        {
          path: "orgs/:orgSlug/view/:projectId",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <ViewerPageWrapperWithLayout />
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        {
          path: "orgs/:orgSlug/view/:projectId/page/:pageId",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <ViewerPageWrapperWithLayout />
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        {
          path: "settings",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <RootLayout>
                  <SettingsPage />
                </RootLayout>
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        {
          path: "billing",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <RootLayout>
                  <BillingPage />
                </RootLayout>
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        {
          path: "account/people",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <RootLayout>
                  <PeoplePage />
                </RootLayout>
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        {
          path: "account/graduate",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <RootLayout>
                  <GraduatePage />
                </RootLayout>
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        {
          path: "profile",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <RootLayout>
                  <ProfilePage />
                </RootLayout>
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        {
          path: "data",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <RootLayout>
                  <DataPage />
                </RootLayout>
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        {
          path: "data/sources/new",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <RootLayout>
                  <DataSourceWizard />
                </RootLayout>
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        {
          path: "data/sources/:sourceId",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <RootLayout>
                  <DataSourceDetailPage />
                </RootLayout>
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        {
          path: "data/sources/:sourceId/tables/:tableName",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <RootLayout>
                  <TableFieldsEditor />
                </RootLayout>
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        {
          path: "agents",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <RootLayout>
                  <AgentsPage />
                </RootLayout>
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        {
          path: "admin",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <RootLayout>
                  <AdminPage />
                </RootLayout>
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        {
          path: "admin/feedback",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <RootLayout>
                  <FeedbackPage />
                </RootLayout>
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        {
          path: "workflow-configuration",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <RootLayout>
                  <WorkflowConfigurationPage />
                </RootLayout>
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        {
          path: "workflow-configuration/:projectId",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <RootLayout>
                  <WorkflowConfigurationPage />
                </RootLayout>
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        {
          path: "workflow-editor/:workflowId",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <Suspense fallback={<div className="h-screen flex items-center justify-center text-muted-foreground">Loading workflow editor...</div>}>
                  <WorkflowEditorPage />
                </Suspense>
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        {
          path: "devices",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <RootLayout>
                  <DeviceManagementPage />
                </RootLayout>
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        {
          path: "devices/:deviceSubview",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <RootLayout>
                  <DeviceManagementPage />
                </RootLayout>
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        {
          path: "irrigation",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <RootLayout>
                  <HydrawiseDashboardPage />
                </RootLayout>
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        {
          path: "smart-devices",
          element: (
            <ProtectedRoute>
              <Navigate to="/devices/connect" replace />
            </ProtectedRoute>
          ),
        },
        {
          path: "cameras",
          element: (
            <ProtectedRoute>
              <Navigate to="/devices/cameras" replace />
            </ProtectedRoute>
          ),
        },
        {
          path: "binary-formats",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <RootLayout>
                  <BinaryFormatsPage />
                </RootLayout>
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        {
          path: "infrastructure",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <RootLayout>
                  <InfrastructureDashboard />
                </RootLayout>
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        {
          path: "files",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <RootLayout>
                  <FileManagerPage />
                </RootLayout>
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        {
          path: "dev/storage",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <RootLayout>
                  <DevStoragePage />
                </RootLayout>
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        {
          path: "viewer/template",
          element: (
            <ProtectedRoute>
              <UserFlowErrorBoundary>
                <TemplateViewerPageWithLayout />
              </UserFlowErrorBoundary>
            </ProtectedRoute>
          ),
        },
        {
          path: "test-workflow",
          element: <WorkflowTestPage />,
        },
        {
          path: "test-analytics",
          element: <AnalyticsTestPage />,
        },

        // Homepage (redirects to dashboard if authenticated)
        {
          index: true,
          element: (
            <PublicRoute>
              <LandingPageV3 />
            </PublicRoute>
          ),
        },

        // 404
        {
          path: "*",
          element: <NotFoundPage />,
        },
    ],
  },
];

const router = createBrowserRouter(
  appRoutes,
  {
    future: {
      v7_relativeSplatPath: true,
    },
  },
);

function App() {
  return <RouterProvider router={router} future={{ v7_startTransition: true }} />;
}

export default App;
